import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { CalendarIcon, MapPinIcon, StoreIcon } from 'lucide-react';
import { creators } from '../data/creators';
import { CreatorCard } from '../components/creator/CreatorCard';
import { ExhibitionProgress } from '../components/creator/ExhibitionProgress';
import { ProductGrid } from '../components/product/ProductGrid';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { NewsletterForm } from '../components/ui/NewsletterForm';
import { SectionHeading } from '../components/ui/SectionHeading';
import { btnSecondary } from '../components/ui/buttonStyles';
import { getCategory, getCreator, getProductsByCreator } from '../utils/catalog';
import { daysUntilStart, formatDate, getStatus } from '../utils/exhibition';
import { NotFound } from './NotFound';
export function CreatorDetail() {
  const {
    creatorId
  } = useParams();
  const creator = creatorId ? getCreator(creatorId) : undefined;
  if (!creator) return <NotFound data-id="element-761" data-mp="1z2ilmjosfo0z" />;
  const status = getStatus(creator);
  const pieces = getProductsByCreator(creator.id);
  const category = getCategory(creator.category);
  const others = creators.filter(c => c.id !== creator.id && getStatus(c) === 'current').sort((a, b) => Number(b.category === creator.category) - Number(a.category === creator.category)).slice(0, 4);
  return <>
      <div className="mx-auto max-w-[1440px] px-5 pt-8 md:px-8 md:pt-12" data-id="element-762" data-mp="1gcaqoov8s3vv">
        <Breadcrumbs items={[{
        label: 'Créateurs',
        to: '/createurs'
      }, {
        label: creator.name
      }]} data-id="element-763" data-mp="1ga6nk95638wn" />
        <div className="mt-6 grid gap-10 lg:grid-cols-12 lg:gap-16" data-id="element-764" data-mp="1bb3gyk9jgumk">
          <div className="lg:col-span-5" data-id="element-765" data-mp="1q2vucqprvyln">
            <div className="aspect-[4/5] overflow-hidden rounded-sm bg-shell" data-id="element-766" data-mp="1q3x4msyysh2b">
              <img src={creator.image} alt={`L’univers de ${creator.name}`} className={`h-full w-full object-cover ${status === 'past' ? 'grayscale-[60%]' : ''}`} data-id="element-767" data-mp="1l9au0s0pisja" />
            </div>
          </div>
          <div className="flex flex-col lg:col-span-7 lg:pt-6" data-id="element-768" data-mp="1i9st8moly4g8">
            {category && <Link to={`/boutique?categorie=${category.id}`} className="self-start text-sm text-muted hover:text-clay" data-id="element-769" data-mp="1mbnrtjf5m3qb">
                {category.name}
              </Link>}
            <h1 className="mt-2 font-display text-6xl leading-[0.95] md:text-8xl" data-id="element-770" data-mp="1w2s7ymgkfumq">{creator.name}</h1>
            <p className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted" data-id="element-771" data-mp="zchtl0z2d909">
              <span data-id="element-772" data-mp="13d1py2btc8lc">{creator.maker}</span>
              <span data-id="element-773" data-mp="13e3d3563sljf">{creator.craft}</span>
              <span className="flex items-center gap-1" data-id="element-774" data-mp="18co7up2l6d0h">
                <MapPinIcon className="h-3.5 w-3.5" aria-hidden data-id="element-775" data-mp="18b3cix8g6q1k" /> {creator.city}
              </span>
            </p>
            <blockquote className="mt-8 font-display text-3xl italic leading-snug text-clay md:text-4xl" data-id="element-776" data-mp="1d9obyufvl5oy">« {creator.quote} »</blockquote>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-muted" data-id="element-777" data-mp="2oqelamflidms">{creator.bio}</p>

            <div className="mt-10 max-w-xl rounded-md bg-card p-5" data-id="element-778" data-mp="d441zqjrj633">
              {status === 'current' && <>
                  <p className="mb-3 flex items-center gap-2 text-sm font-medium" data-id="element-779" data-mp="85sqlbdtknir">
                    <CalendarIcon className="h-4 w-4" aria-hidden data-id="element-780" data-mp="3czqx1x7k5dj" /> En vitrine
                  </p>
                  <ExhibitionProgress creator={creator} data-id="element-781" data-mp="3uj12rcozz308" />
                  <p className="mt-4 flex items-center gap-2 text-sm text-muted" data-id="element-782" data-mp="3ui812q4wb3oy">
                    <StoreIcon className="h-4 w-4" aria-hidden data-id="element-783" data-mp="3qge52v5nanv4" />
                    En permanence à la boutique le {creator.permanenceDays.join(' et le ').toLowerCase()}
                  </p>
                </>}
              {status === 'upcoming' && <>
                  <p className="text-sm" data-id="element-784" data-mp="3bn163ol2rbab">
                    <span className="font-medium" data-id="element-785" data-mp="1esodvtagp4ec">Arrive le {formatDate(creator.exhibitionStart, 'd MMMM')}</span>
                    <span className="text-muted" data-id="element-786" data-mp="19tlfgrozlwfc"> — dans {daysUntilStart(creator)} jours</span>
                  </p>
                  <p className="mt-1 text-sm text-muted" data-id="element-787" data-mp="1hvugasyvj1dj">Soyez prévenu·e le jour de son installation.</p>
                  <div className="mt-4" data-id="element-788" data-mp="1msb5zrlq1nsq">
                    <NewsletterForm cta="Me prévenir" successMessage={`C’est noté ! Nous vous écrirons à l’arrivée de ${creator.name}.`} data-id="element-789" data-mp="1mr9p36i0y733" />
                  </div>
                </>}
              {status === 'past' && <p className="text-sm text-muted" data-id="element-790" data-mp="z3rtezvn2ouu">
                  {creator.name} a exposé du {formatDate(creator.exhibitionStart)} au {formatDate(creator.exhibitionEnd)}. Ses
                  pièces ne sont plus disponibles — c’est la règle de l’éphémère.
                </p>}
            </div>
          </div>
        </div>
      </div>

      {status === 'current' && <section className="mx-auto max-w-[1440px] px-5 pt-20 md:px-8" aria-labelledby="pieces-title" data-id="element-791" data-mp="g4npg5iyfla1">
          <SectionHeading id="pieces-title" title={<>
                Ses pièces <em className="text-clay" data-id="element-793" data-mp="1hp2amr4rd14">en ligne</em>
              </>} description={pieces.length ? `Disponibles jusqu’au ${formatDate(creator.exhibitionEnd)}.` : undefined} data-id="element-792" data-mp="k7anwpa4s4g7" />
          {pieces.length > 0 ? <ProductGrid products={pieces} data-id="element-794" data-mp="ah29f8rs7pio" /> : <div className="rounded-sm bg-card px-6 py-14 text-center" data-id="element-795" data-mp="5j0r4q26ju89">
              <p className="font-display text-3xl" data-id="element-796" data-mp="kh2v31d4ihq1">À découvrir en boutique</p>
              <p className="mt-2 text-sm text-muted" data-id="element-797" data-mp="kf00s54xu2fi">Les créations de {creator.name} sont présentées uniquement sur place.</p>
            </div>}
        </section>}

      <section className="mx-auto max-w-[1440px] px-5 pt-20 md:px-8" aria-labelledby="autres-title" data-id="element-798" data-mp="t8v73ghspywq">
        <SectionHeading id="autres-title" title={<>Également en vitrine</>} link={{
        to: '/createurs',
        label: 'Tous les créateurs'
      }} data-id="element-799" data-mp="pa5uhq4l0le5" />
        <ul className="grid grid-cols-2 gap-x-4 gap-y-12 md:grid-cols-4 md:gap-x-6" data-id="element-800" data-mp="y8zmt4avqplw">
          {others.map(c => <li key={c.id} data-id="element-801" data-mp="3s1ije88wua2q">
              <CreatorCard creator={c} data-id="element-802" data-mp="3s2afdk6f69tj" />
            </li>)}
        </ul>
        {status === 'past' && <div className="mt-10" data-id="element-803" data-mp="3w0846lwa2twe">
            <Link to="/boutique" className={btnSecondary} data-id="element-804" data-mp="4w9l32x103i4">
              Voir la saison en cours
            </Link>
          </div>}
      </section>
    </>;
}